import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Ticker speed in pixels per second.
const PACE = { slow: 40, normal: 65, fast: 100 };

/** The messages to show: `items`, or the single message saved before items existed. */
export function announcementMessages(announcement, lang) {
  if (!announcement?.enabled) return [];
  const items = Array.isArray(announcement.items) && announcement.items.length
    ? announcement.items
    : (announcement.text ? [{ text: announcement.text, url: announcement.url, openNewTab: announcement.openNewTab }] : []);
  return items
    .map((item) => ({ ...item, label: (lang === 'en' && item.textEn) || item.text }))
    .filter((item) => item.label);
}

// The ticker's second copy is only there to make the loop seamless: its links
// still work on click but are skipped by the keyboard and screen readers.
const Message = ({ item, copy = false }) => {
  const tabIndex = copy ? -1 : undefined;
  if (!item.url) return <span>{item.label}</span>;
  if (/^https?:\/\//i.test(item.url)) {
    return <a href={item.url} tabIndex={tabIndex} target={item.openNewTab ? '_blank' : undefined} rel={item.openNewTab ? 'noopener noreferrer' : undefined}>{item.label}</a>;
  }
  return <Link to={item.url} tabIndex={tabIndex}>{item.label}</Link>;
};

/**
 * The bar above the header (admin → Theme → styles → Announcement bar).
 * 'scroll' runs the messages past as a ticker (paused on hover or focus;
 * still, one after another, for visitors who ask for reduced motion);
 * 'static' shows one message at a time, changing every few seconds.
 */
const AnnouncementBar = ({ announcement }) => {
  const { t, i18n } = useTranslation();
  const messages = announcementMessages(announcement, (i18n.language || 'sq').slice(0, 2));
  const scroll = announcement?.style === 'scroll';
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const groupRef = useRef(null);
  const [duration, setDuration] = useState(null);
  const pace = PACE[announcement?.speed] || PACE.normal;
  const texts = messages.map((item) => item.label).join('|');

  // One pass moves the track by one copy's width, so the time follows its length.
  useLayoutEffect(() => {
    if (!scroll || !groupRef.current) return undefined;
    const measure = () => setDuration(Math.max(8, Math.round(groupRef.current.scrollWidth / pace)));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [scroll, pace, texts]);

  useEffect(() => {
    if (scroll || messages.length < 2 || paused) return undefined;
    const timer = setInterval(() => setIndex((current) => (current + 1) % messages.length), 5000);
    return () => clearInterval(timer);
  }, [scroll, messages.length, paused]);

  if (messages.length === 0) return null;

  const label = t('store.announcement', 'Store announcement');
  if (scroll) {
    const group = (hidden) => (
      <ul className="store-announcement__group" aria-hidden={hidden || undefined} ref={hidden ? undefined : groupRef}>
        {messages.map((item, i) => (
          <li key={i}><Message item={item} copy={hidden} /></li>
        ))}
      </ul>
    );
    return (
      <div className="store-announcement store-announcement--scroll" role="region" aria-label={label} style={duration ? { '--ticker-duration': `${duration}s` } : undefined}>
        <div className="store-announcement__track">
          {group(false)}
          {group(true)}
        </div>
      </div>
    );
  }

  const current = messages[index % messages.length];
  return (
    <div
      className="store-announcement"
      role="region"
      aria-label={label}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span key={index} className="store-announcement__message"><Message item={current} /></span>
    </div>
  );
};

export default AnnouncementBar;
